import { useCallback, useEffect, useRef } from 'react';
import { ANIMATION_SPEED } from '../../constants/config';
import { useAppStore } from '../../store/useAppStore';

const DRONE_URL = '/sounds/ambient-drone.mp3';
const FALLBACK_DRONE_URL = '/sounds/ambient-drone.mp3';
const SILENCE = 0.00001;
const WIND_MAX_VELOCITY = 0.01;
const DRONE_FADE_IN_DURATION = 4;
const DRONE_TARGET_GAIN = 0.05;
const ENGINE_CLEANUP_DELAY_MS = 1000;

export type UISoundType = 'nav' | 'footer' | 'link' | 'close';
export type UISoundEvent = 'enter' | 'leave' | 'click';

interface SoundEngine {
  ctx: AudioContext;
  compressor: DynamicsCompressorNode;
  droneElement: HTMLAudioElement;
  droneSource: MediaElementAudioSourceNode;
  droneGain: GainNode;
  windSource: AudioBufferSourceNode;
  windFilter: BiquadFilterNode;
  windGain: GainNode;
  initialized: boolean;
  destroyed: boolean;
}

let engineSingleton: SoundEngine | null = null;
let engineInstanceCount = 0;
let cleanupTimeoutId: number | null = null;

const clearCleanupTimeout = () => {
  if (cleanupTimeoutId !== null && typeof window !== 'undefined') {
    window.clearTimeout(cleanupTimeoutId);
    cleanupTimeoutId = null;
  }
};

const createWhiteNoiseBuffer = (
  ctx: AudioContext,
  duration: number
): AudioBuffer => {
  const length = Math.floor(ctx.sampleRate * duration);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const channelData = buffer.getChannelData(0);

  for (let index = 0; index < length; index += 1) {
    channelData[index] = Math.random() * 2 - 1;
  }

  return buffer;
};

const createDroneElement = (): HTMLAudioElement => {
  const element = new Audio();
  element.loop = true;
  element.preload = 'auto';
  element.crossOrigin = 'anonymous';
  element.src = DRONE_URL;

  element.addEventListener(
    'error',
    () => {
      if (element.src.endsWith(DRONE_URL)) {
        element.src = FALLBACK_DRONE_URL;
        element.load();
      }
    },
    { once: true }
  );

  element.load();
  return element;
};

const createSoundEngine = (): SoundEngine | null => {
  if (typeof window === 'undefined') return null;
  if (engineSingleton && !engineSingleton.destroyed) return engineSingleton;

  const AudioContextCtor =
    window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!AudioContextCtor) return null;

  let ctx: AudioContext;
  try {
    ctx = new AudioContextCtor();
  } catch (error) {
    console.error('AudioContext creation failed:', error);
    return null;
  }

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.setValueAtTime(-24, ctx.currentTime);
  compressor.knee.setValueAtTime(40, ctx.currentTime);
  compressor.ratio.setValueAtTime(12, ctx.currentTime);
  compressor.attack.setValueAtTime(0.003, ctx.currentTime);
  compressor.release.setValueAtTime(0.25, ctx.currentTime);
  compressor.connect(ctx.destination);

  const droneElement = createDroneElement();
  const droneSource = ctx.createMediaElementSource(droneElement);
  const droneGain = ctx.createGain();
  droneGain.gain.setValueAtTime(SILENCE, ctx.currentTime);
  droneSource.connect(droneGain);
  droneGain.connect(compressor);

  const windSource = ctx.createBufferSource();
  windSource.buffer = createWhiteNoiseBuffer(ctx, 2);
  windSource.loop = true;

  const windFilter = ctx.createBiquadFilter();
  windFilter.type = 'lowpass';
  windFilter.frequency.setValueAtTime(150, ctx.currentTime);
  windFilter.Q.setValueAtTime(0.3, ctx.currentTime);

  const windGain = ctx.createGain();
  windGain.gain.setValueAtTime(SILENCE, ctx.currentTime);

  windSource.connect(windFilter);
  windFilter.connect(windGain);
  windGain.connect(compressor);
  windSource.start();

  engineSingleton = {
    ctx,
    compressor,
    droneElement,
    droneSource,
    droneGain,
    windSource,
    windFilter,
    windGain,
    initialized: false,
    destroyed: false,
  };

  return engineSingleton;
};

const getSoundEngine = (): SoundEngine | null => {
  if (engineSingleton && !engineSingleton.destroyed) {
    return engineSingleton;
  }

  return createSoundEngine();
};

const safeDisconnect = (node: AudioNode | null) => {
  if (!node) return;

  try {
    node.disconnect();
  } catch {
    /* already disconnected */
  }
};

const cleanupSoundEngine = async () => {
  const engine = engineSingleton;
  if (!engine || engine.destroyed) return;

  engine.destroyed = true;

  try {
    engine.windSource.stop();
  } catch {
    /* already stopped */
  }

  engine.droneElement.pause();
  engine.droneElement.removeAttribute('src');
  engine.droneElement.load();

  safeDisconnect(engine.droneSource);
  safeDisconnect(engine.droneGain);
  safeDisconnect(engine.windSource);
  safeDisconnect(engine.windFilter);
  safeDisconnect(engine.windGain);
  safeDisconnect(engine.compressor);

  if (engine.ctx.state !== 'closed') {
    try {
      await engine.ctx.close();
    } catch {
      /* ignore close failures */
    }
  }

  engineSingleton = null;
};

const scheduleGhostNote = (
  engine: SoundEngine,
  type: UISoundType,
  event: UISoundEvent
) => {
  const { ctx, compressor } = engine;
  const now = ctx.currentTime;

  const oscillator = ctx.createOscillator();
  oscillator.type = 'sine';

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(SILENCE, now);

  const isClick = event === 'click';
  const isNav = type === 'nav';
  const isLink = type === 'link';
  const isClose = type === 'close';

  let frequency = isNav ? 1760 : isClose ? 1320 : isLink ? 440 : 440;
  let attack = isNav ? 0.02 : 0.04;
  let decay = isNav ? 0.4 : 0.5;
  let peakGain = isNav ? 0.03 : 0.03;
  let eventScale = event === 'enter' ? 1 : 0.75;
  let releaseTimeConstant = isNav ? 0.12 : 0.16;

  if (isClick) {
    frequency = isClose ? 1320 : isLink ? 880 : 440;
    attack = 0.01;
    decay = 0.15;
    peakGain = 0.025;
    eventScale = 1;
    releaseTimeConstant = 0.06;
  }

  oscillator.frequency.setValueAtTime(frequency, now);
  oscillator.connect(gain);
  gain.connect(compressor);

  gain.gain.setTargetAtTime(Math.max(peakGain * eventScale, SILENCE), now, attack);
  gain.gain.setTargetAtTime(SILENCE, now + attack, releaseTimeConstant);

  oscillator.start(now);
  oscillator.stop(now + attack + decay + 0.1);
  oscillator.onended = () => {
    safeDisconnect(oscillator);
    safeDisconnect(gain);
  };
};

const scheduleProjectOpenWind = (engine: SoundEngine) => {
  const { ctx, compressor } = engine;
  const turnDuration = 0.5;
  const aspirateDuration = 1.5;
  const totalDuration = turnDuration + aspirateDuration;
  const startTime = ctx.currentTime;

  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createWhiteNoiseBuffer(ctx, totalDuration + 0.1);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.Q.setValueAtTime(0.6, startTime);

  const panner = ctx.createStereoPanner();
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(SILENCE, startTime);

  noiseSource.connect(filter);
  filter.connect(panner);
  panner.connect(gain);
  gain.connect(compressor);

  noiseSource.start(startTime);

  // Phase 1 (0–0.4s): vent qui se retourne
  filter.frequency.setValueAtTime(600, startTime);
  filter.frequency.exponentialRampToValueAtTime(1800, startTime + turnDuration * 0.5);
  filter.frequency.exponentialRampToValueAtTime(700, startTime + turnDuration);

  gain.gain.exponentialRampToValueAtTime(0.035, startTime + turnDuration * 0.35);
  gain.gain.exponentialRampToValueAtTime(0.02, startTime + turnDuration);

  panner.pan.setValueAtTime(0.5, startTime);
  panner.pan.linearRampToValueAtTime(-0.45, startTime + turnDuration);

  // Phase 2 (0.4–1.9s): aspiré
  const t1 = startTime + turnDuration;
  filter.frequency.exponentialRampToValueAtTime(2800, t1 + 0.08);
  filter.frequency.exponentialRampToValueAtTime(60, t1 + aspirateDuration * 0.85);

  gain.gain.exponentialRampToValueAtTime(0.032, t1 + aspirateDuration * 0.08);
  gain.gain.exponentialRampToValueAtTime(0.022, t1 + aspirateDuration * 0.35);
  gain.gain.exponentialRampToValueAtTime(SILENCE, t1 + aspirateDuration);

  panner.pan.linearRampToValueAtTime(0.5, t1 + 0.06);
  panner.pan.linearRampToValueAtTime(-0.3, t1 + aspirateDuration * 0.45);
  panner.pan.linearRampToValueAtTime(0, t1 + aspirateDuration * 0.8);

  noiseSource.stop(startTime + totalDuration);
  noiseSource.onended = () => {
    safeDisconnect(noiseSource);
    safeDisconnect(filter);
    safeDisconnect(panner);
    safeDisconnect(gain);
  };
};

const scheduleProjectCloseWind = (engine: SoundEngine) => {
  const { ctx, compressor } = engine;
  const duration = 0.9;
  const startTime = ctx.currentTime;

  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createWhiteNoiseBuffer(ctx, duration + 0.1);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(90, startTime);
  filter.Q.setValueAtTime(0.5, startTime);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(SILENCE, startTime);

  noiseSource.connect(filter);
  filter.connect(gain);
  gain.connect(compressor);

  noiseSource.start(startTime);

  gain.gain.exponentialRampToValueAtTime(0.11, startTime + duration * 0.2);
  gain.gain.exponentialRampToValueAtTime(0.035, startTime + duration * 0.5);
  gain.gain.exponentialRampToValueAtTime(SILENCE, startTime + duration);

  filter.frequency.exponentialRampToValueAtTime(2200, startTime + duration * 0.65);

  noiseSource.stop(startTime + duration);
  noiseSource.onended = () => {
    safeDisconnect(noiseSource);
    safeDisconnect(filter);
    safeDisconnect(gain);
  };
};

const scheduleIntroVortex = (engine: SoundEngine) => {
  const { ctx, compressor } = engine;
  const t0 = ctx.currentTime;
  const introDuration = 1 / ANIMATION_SPEED.INIT;
  const duration = Math.max(introDuration - 0.25, 1.8);
  const startTime = t0;

  const noiseSource = ctx.createBufferSource();
  noiseSource.buffer = createWhiteNoiseBuffer(ctx, duration + 0.1);

  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(260, startTime);
  filter.Q.setValueAtTime(0.65, startTime);

  const panner = ctx.createStereoPanner();
  panner.pan.setValueAtTime(-1, startTime);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(SILENCE, startTime);

  noiseSource.connect(filter);
  filter.connect(panner);
  panner.connect(gain);
  gain.connect(compressor);

  noiseSource.start(startTime);

  gain.gain.exponentialRampToValueAtTime(0.04, startTime + duration * 0.12);
  gain.gain.exponentialRampToValueAtTime(0.085, startTime + duration * 0.3);
  gain.gain.exponentialRampToValueAtTime(0.07, startTime + duration * 0.48);
  gain.gain.exponentialRampToValueAtTime(0.035, startTime + duration * 0.68);
  gain.gain.exponentialRampToValueAtTime(SILENCE, startTime + duration);

  filter.frequency.exponentialRampToValueAtTime(2800, startTime + duration * 0.28);
  filter.frequency.exponentialRampToValueAtTime(3600, startTime + duration * 0.46);
  filter.frequency.exponentialRampToValueAtTime(1400, startTime + duration * 0.74);

  panner.pan.linearRampToValueAtTime(0.95, startTime + duration * 0.22);
  panner.pan.linearRampToValueAtTime(-0.7, startTime + duration * 0.46);
  panner.pan.linearRampToValueAtTime(0.75, startTime + duration * 0.66);
  panner.pan.linearRampToValueAtTime(0, startTime + duration * 0.88);

  noiseSource.stop(startTime + duration);
  noiseSource.onended = () => {
    safeDisconnect(noiseSource);
    safeDisconnect(filter);
    safeDisconnect(panner);
    safeDisconnect(gain);
  };
};

export const useSound = () => {
  const initializedRef = useRef(false);
  const isSoundEnabled = useAppStore(state => state.isSoundEnabled);
  const setSoundEnabledState = useAppStore(state => state.setSoundEnabled);

  const init = useCallback(async () => {
    if (!useAppStore.getState().isSoundEnabled) return;

    // Defer past at least one painted frame so the page transition is visible
    // before new AudioContext() potentially blocks the main thread.
    await new Promise<void>(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));

    const engine = getSoundEngine();
    if (!engine) return;

    const { ctx, droneElement, droneGain } = engine;
    const now = ctx.currentTime;

    if (ctx.state !== 'running') {
      await ctx.resume();
    }

    droneGain.gain.cancelScheduledValues(now);
    if (droneElement.paused) {
      // Start almost imperceptibly, then bloom into the final bed over 3 seconds.
      droneGain.gain.setValueAtTime(SILENCE, now);
      droneGain.gain.setTargetAtTime(0.004, now, 0.45);
      droneGain.gain.setTargetAtTime(
        DRONE_TARGET_GAIN,
        now + 0.9,
        DRONE_FADE_IN_DURATION / 3
      );
    } else {
      droneGain.gain.setValueAtTime(
        Math.max(droneGain.gain.value, SILENCE),
        now
      );
      droneGain.gain.setTargetAtTime(
        DRONE_TARGET_GAIN,
        now,
        DRONE_FADE_IN_DURATION / 3
      );
    }

    if (droneElement.paused) {
      try {
        await droneElement.play();
      } catch (error) {
        console.error('Failed to start ambient drone:', error);
      }
    }

    engine.initialized = true;
    initializedRef.current = true;
  }, []);

  const setSoundEnabled = useCallback(async (enabled: boolean) => {
    setSoundEnabledState(enabled);
    await new Promise<void>(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));
    const engine = getSoundEngine();
    if (!engine) return;

    const now = engine.ctx.currentTime;

    if (!enabled) {
      engine.droneGain.gain.cancelScheduledValues(now);
      engine.windGain.gain.cancelScheduledValues(now);
      engine.droneGain.gain.setTargetAtTime(SILENCE, now, 0.15);
      engine.windGain.gain.setTargetAtTime(SILENCE, now, 0.1);
      engine.droneElement.pause();
      return;
    }

    if (!engine.initialized) {
      await init();
      return;
    }

    if (engine.ctx.state !== 'running') {
      await engine.ctx.resume();
    }

    if (engine.droneElement.paused) {
      try {
        await engine.droneElement.play();
      } catch (error) {
        console.error('Failed to resume ambient drone:', error);
      }
    }

    engine.droneGain.gain.cancelScheduledValues(now);
    engine.droneGain.gain.setTargetAtTime(
      DRONE_TARGET_GAIN,
      now,
      DRONE_FADE_IN_DURATION / 3
    );
  }, [init, setSoundEnabledState]);

  const updateWind = useCallback((velocity: number) => {
    if (!useAppStore.getState().isSoundEnabled) return;

    const engine = getSoundEngine();
    if (!engine || !engine.initialized) return;

    const now = engine.ctx.currentTime;
    const normalizedVelocity = Math.min(Math.abs(velocity) / WIND_MAX_VELOCITY, 1);
    const smoothVelocity = normalizedVelocity * normalizedVelocity;
    const targetGain = 0.095 * smoothVelocity;
    const targetFrequency = 150 + (800 - 150) * smoothVelocity;

    engine.windGain.gain.setTargetAtTime(
      Math.max(targetGain, SILENCE),
      now,
      0.08
    );
    engine.windFilter.frequency.setTargetAtTime(targetFrequency, now, 0.1);
  }, []);

  const playUI = useCallback((type: UISoundType, event: UISoundEvent) => {
    if (!useAppStore.getState().isSoundEnabled) return;

    const engine = getSoundEngine();
    if (!engine) return;

    const trigger = () => {
      scheduleGhostNote(engine, type, event);
    };

    if (engine.ctx.state !== 'running') {
      void engine.ctx.resume().then(trigger);
      return;
    }

    trigger();
  }, []);

  const playIntroVortex = useCallback(() => {
    if (!useAppStore.getState().isSoundEnabled) return;

    const engine = getSoundEngine();
    if (!engine) return;

    const trigger = () => {
      scheduleIntroVortex(engine);
    };

    if (engine.ctx.state !== 'running') {
      void engine.ctx.resume().then(trigger);
      return;
    }

    trigger();
  }, []);

  const playProjectOpenWind = useCallback(() => {
    if (!useAppStore.getState().isSoundEnabled) return;

    const engine = getSoundEngine();
    if (!engine) return;

    const trigger = () => {
      scheduleProjectOpenWind(engine);
    };

    if (engine.ctx.state !== 'running') {
      void engine.ctx.resume().then(trigger);
      return;
    }

    trigger();
  }, []);

  const playProjectCloseWind = useCallback(() => {
    if (!useAppStore.getState().isSoundEnabled) return;

    const engine = getSoundEngine();
    if (!engine) return;

    const trigger = () => {
      scheduleProjectCloseWind(engine);
    };

    if (engine.ctx.state !== 'running') {
      void engine.ctx.resume().then(trigger);
      return;
    }

    trigger();
  }, []);

  useEffect(() => {
    engineInstanceCount += 1;
    clearCleanupTimeout();

    return () => {
      engineInstanceCount = Math.max(0, engineInstanceCount - 1);

      if (engineInstanceCount === 0 && typeof window !== 'undefined') {
        cleanupTimeoutId = window.setTimeout(() => {
          if (engineInstanceCount === 0) {
            void cleanupSoundEngine();
          }
        }, ENGINE_CLEANUP_DELAY_MS);
      }
    };
  }, []);

  return {
    init,
    playUI,
    playIntroVortex,
    playProjectOpenWind,
    playProjectCloseWind,
    updateWind,
    setSoundEnabled,
    isSoundEnabled,
    isReady: initializedRef.current,
  };
};

export default useSound;
